import { useParams, useSearch } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { InvoiceView } from "@/components/invoice/invoice-view";
import { PoweredByAttribution } from "@/components/powered-by-attribution";
import { Loader2, Download, CreditCard, ExternalLink, AlertCircle, CheckCircle, PartyPopper, Share2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useState, useRef, useEffect } from "react";
import type { Invoice } from "@shared/schema";
import { SiStripe, SiPaypal } from "react-icons/si";
import { useToast } from "@/hooks/use-toast";
import confetti from "canvas-confetti";

interface PublicPaymentSettings {
  stripeEnabled: boolean;
  paypalEnabled: boolean;
  paypalEmail: string | null;
  paypalMeUsername: string | null;
  /** Public client-id for the PayPal JS SDK — only set when paypalSdkEnabled is true */
  paypalClientId: string | null;
  /** True when workspace has both clientId + clientSecret — uses server-verified checkout */
  paypalSdkEnabled: boolean;
  enableInvoicePayments: boolean;
  preferredProvider: string;
}

interface PublicBranding {
  faviconPath: string | null;
  sidebarIconPath: string | null;
  workspaceName: string | null;
  logoUrl: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  accentColor: string | null;
}

function PaymentSuccessOverlay({ invoiceNumber }: { invoiceNumber: string }) {
  useEffect(() => {
    const duration = 4000;
    const end = Date.now() + duration;

    const frame = () => {
      confetti({
        particleCount: 6,
        angle: 60,
        spread: 65,
        origin: { x: 0 },
        colors: ["#22c55e", "#16a34a", "#4ade80", "#fbbf24", "#3b82f6"],
      });
      confetti({
        particleCount: 6,
        angle: 120,
        spread: 65,
        origin: { x: 1 },
        colors: ["#22c55e", "#16a34a", "#4ade80", "#fbbf24", "#3b82f6"],
      });
      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };
    frame();
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-white/95 backdrop-blur-sm"
      data-testid="payment-success-overlay"
    >
      <div className="text-center px-6 max-w-md">
        <div className="flex justify-center mb-6">
          <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle className="w-14 h-14 text-green-500" />
          </div>
        </div>
        <h1 className="text-4xl font-bold text-gray-900 mb-3">Invoice Paid!</h1>
        <p className="text-xl text-gray-600 mb-2">
          Invoice #{invoiceNumber} has been paid successfully.
        </p>
        <p className="text-gray-500 mb-8">
          Thank you! You'll receive a confirmation shortly.
        </p>
        <div className="flex items-center justify-center gap-2 text-green-600 font-medium">
          <PartyPopper className="w-5 h-5" />
          <span>Payment confirmed</span>
          <PartyPopper className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

export default function PublicInvoicePage() {
  const params = useParams();
  const token = params.id;
  const searchString = useSearch();
  const { toast } = useToast();
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentState, setPaymentState] = useState<"idle" | "stripe_confirming" | "stripe_success" | "paypal_pending" | "paypal_success" | "cancelled">("idle");
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [emailPopoverOpen, setEmailPopoverOpen] = useState(false);
  const [emailMeAddress, setEmailMeAddress] = useState("");
  const hasConfirmed = useRef(false);
  const invoiceRef = useRef<HTMLDivElement>(null);
  const paypalButtonContainerRef = useRef<HTMLDivElement>(null);
  const paypalButtonsInstance = useRef<any>(null);
  const [paypalSdkReady, setPaypalSdkReady] = useState(false);

  const { data: invoice, isLoading, error } = useQuery<Invoice & { lateFeeDescription?: string | null }>({
    queryKey: ["/api/public/invoice", token],
    queryFn: async () => {
      const response = await fetch(`/api/public/invoice/${token}`);
      if (!response.ok) {
        throw new Error("Invoice not found");
      }
      return response.json();
    },
    enabled: !!token,
  });

  const confirmPaymentMutation = useMutation({
    mutationFn: async ({ method, sessionId, purpose }: { method: "stripe" | "paypal"; sessionId?: string; purpose?: "deposit" | "full" }) => {
      const response = await fetch(`/api/public/invoice/${token}/confirm-payment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ method, sessionId, purpose }),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || "Failed to confirm payment");
      }
      return response.json();
    },
    onSuccess: (_, variables) => {
      if (variables.method === "stripe") {
        setPaymentState("stripe_success");
      } else {
        setPaymentState("paypal_success");
      }
      window.history.replaceState({}, "", window.location.pathname);
    },
    onError: (err: Error) => {
      setConfirmError(err.message);
      setPaymentState("idle");
      toast({
        title: "Payment Verification Failed",
        description: err.message || "Could not verify your payment. Please contact support.",
        variant: "destructive",
      });
      window.history.replaceState({}, "", window.location.pathname);
    },
  });

  const { data: paymentSettings } = useQuery<PublicPaymentSettings>({
    queryKey: ["/api/public/payment-settings", invoice?.workspaceId],
    queryFn: async () => {
      const url = invoice?.workspaceId
        ? `/api/public/payment-settings?workspaceId=${invoice.workspaceId}`
        : "/api/public/payment-settings";
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Failed to fetch payment settings");
      }
      return response.json();
    },
    enabled: showPaymentDialog && !!invoice,
  });

  // Load PayPal JS SDK when the dialog is open and SDK credentials are available
  useEffect(() => {
    const clientId = paymentSettings?.paypalClientId;
    if (!paymentSettings?.paypalSdkEnabled || !clientId || !showPaymentDialog) return;
    const existingScript = document.getElementById("paypal-sdk-script");
    if (existingScript) {
      if ((window as any).paypal) setPaypalSdkReady(true);
      return;
    }
    const script = document.createElement("script");
    script.id = "paypal-sdk-script";
    script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=USD&intent=capture&disable-funding=credit,card`;
    script.async = true;
    script.onload = () => setPaypalSdkReady(true);
    script.onerror = () => console.error("[PayPal] Failed to load PayPal JS SDK");
    document.body.appendChild(script);
  }, [paymentSettings?.paypalClientId, paymentSettings?.paypalSdkEnabled, showPaymentDialog]);

  // Render PayPal buttons into container once the SDK is ready
  useEffect(() => {
    const container = paypalButtonContainerRef.current;
    if (!paypalSdkReady || !container || !showPaymentDialog || !(window as any).paypal) return;
    // Destroy previous instance
    try { paypalButtonsInstance.current?.close(); } catch {}
    paypalButtonsInstance.current = null;
    container.innerHTML = "";
    paypalButtonsInstance.current = (window as any).paypal.Buttons({
      style: { layout: "horizontal", color: "blue", shape: "rect", label: "paypal", height: 44 },
      createOrder: async () => {
        const resp = await fetch(`/api/public/invoice/${token}/paypal/create-order`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
        });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.message || "Failed to create PayPal order");
        return data.orderId;
      },
      onApprove: async ({ orderID }: { orderID: string }) => {
        const resp = await fetch(`/api/public/invoice/${token}/paypal/capture-order`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ orderId: orderID }),
        });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.message || "Payment capture failed");
        setShowPaymentDialog(false);
        setPaymentState("paypal_success");
      },
      onCancel: () => {
        // User cancelled — dialog stays open, they can try again
      },
      onError: (err: any) => {
        console.error("[PayPal SDK error]", err);
        toast({ title: "PayPal Error", description: "Payment failed. Please try again.", variant: "destructive" });
      },
    });
    if (paypalButtonsInstance.current.isEligible()) {
      paypalButtonsInstance.current.render(container);
    }
  }, [paypalSdkReady, showPaymentDialog, token]);

  // Handle payment return from Stripe or PayPal
  useEffect(() => {
    const urlParams = new URLSearchParams(searchString);
    const paymentStatus = urlParams.get("payment");
    const sessionId = urlParams.get("session_id");
    // Deposit/date-hold workflow — round-trip the purpose so the
    // confirm-payment call records the right side. The server
    // double-checks against the Stripe session metadata so a
    // tampered URL can't downgrade a full payment to a deposit.
    const purposeParam = urlParams.get("purpose");
    const purpose: "deposit" | "full" =
      purposeParam === "deposit" ? "deposit" : "full";

    if (paymentStatus === "stripe_success" && sessionId && !hasConfirmed.current) {
      hasConfirmed.current = true;
      setPaymentState("stripe_confirming");
      confirmPaymentMutation.mutate({ method: "stripe", sessionId, purpose });
    } else if (paymentStatus === "paypal_pending") {
      setPaymentState("paypal_pending");
      window.history.replaceState({}, "", window.location.pathname);
    } else if (paymentStatus === "cancelled") {
      setPaymentState("cancelled");
      toast({
        title: "Payment Cancelled",
        description: "Your payment was cancelled. You can try again anytime.",
        variant: "destructive",
      });
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [searchString]);

  const { data: branding } = useQuery<PublicBranding>({
    queryKey: ["/api/branding/public", invoice?.workspaceId],
    queryFn: async () => {
      const url = invoice?.workspaceId
        ? `/api/branding/public?workspaceId=${invoice.workspaceId}`
        : "/api/branding/public";
      const response = await fetch(url);
      if (!response.ok) {
        return {
          faviconPath: null,
          sidebarIconPath: null,
          workspaceName: null,
          logoUrl: null,
          primaryColor: null,
          secondaryColor: null,
          accentColor: null,
        } as PublicBranding;
      }
      return response.json();
    },
    enabled: !!invoice,
  });

  const handlePayWithPayPal = () => {
    if (!invoice || !paymentSettings?.paypalMeUsername) return;

    const eventRate = parseFloat(invoice.eventRate || "0");
    const discount = parseFloat(invoice.discount || "0");
    const lateFee = parseFloat(invoice.lateFee || "0");
    const amount = eventRate - discount + lateFee;

    const paypalUsername = paymentSettings.paypalMeUsername;
    const paypalUrl = `https://www.paypal.com/paypalme/${paypalUsername}/${amount.toFixed(2)}USD`;

    // Open PayPal in new tab and transition to pending state
    window.open(paypalUrl, "_blank");
    setShowPaymentDialog(false);
    setPaymentState("paypal_pending");
  };

  const handleConfirmPayPal = () => {
    confirmPaymentMutation.mutate({ method: "paypal" });
  };

  const stripeCheckoutMutation = useMutation({
    mutationFn: async (purpose: "deposit" | "full" = "full") => {
      const response = await fetch(`/api/public/invoice/${token}/create-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ purpose }),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || "Failed to create checkout session");
      }
      return response.json();
    },
    onSuccess: (data) => {
      if (data.url) {
        window.location.href = data.url;
      }
    },
    onError: (error: Error) => {
      // Never surface internal infrastructure details to a client-facing page.
      // If the message contains anything that sounds like a config/setup error,
      // replace it with a generic, customer-friendly fallback.
      const INTERNAL_PHRASES = ["replit", "integrations panel", "connection not found", "service account", "environment variable", "stripe connection"];
      const msg = error.message || "";
      const isInternal = INTERNAL_PHRASES.some((p) => msg.toLowerCase().includes(p.toLowerCase()));
      const safeMessage = isInternal
        ? "Card payments are temporarily unavailable. Please contact us directly to complete your payment."
        : msg || "Failed to start payment. Please try again.";
      toast({
        title: "Payment Error",
        description: safeMessage,
        variant: "destructive",
      });
    },
  });

  const handlePayWithStripe = (purpose: "deposit" | "full" = "full") => {
    if (!token) return;
    stripeCheckoutMutation.mutate(purpose);
  };

  const emailMeMutation = useMutation({
    mutationFn: async (email: string) => {
      const response = await fetch(`/api/public/invoice/${token}/email-pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        // Tag the error with the HTTP status so onError can show a
        // friendlier "too many sends" toast on a 429 instead of the
        // generic "Email Failed" treatment.
        const err = new Error(data.message || "Failed to email invoice") as Error & {
          status?: number;
        };
        err.status = response.status;
        throw err;
      }
      return response.json();
    },
    onSuccess: (_data, variables) => {
      toast({
        title: "Invoice Emailed",
        description: `We sent a copy to ${variables}.`,
      });
      setEmailPopoverOpen(false);
      setEmailMeAddress("");
      // Refetch the invoice so the durable "Last sent to ... at ..."
      // confirmation row picks up the freshly-persisted timestamp.
      queryClient.invalidateQueries({ queryKey: ["/api/public/invoice", token] });
    },
    onError: (err: Error & { status?: number }) => {
      const isRateLimited = err.status === 429;
      toast({
        title: isRateLimited ? "Too Many Requests" : "Email Failed",
        description:
          err.message ||
          (isRateLimited
            ? "This invoice link has been used to email a copy too many times. Please try again shortly."
            : "We couldn't email the invoice. Please try again."),
        variant: "destructive",
      });
    },
  });

  const handleEmailMe = () => {
    const trimmed = emailMeAddress.trim();
    if (!trimmed) return;
    emailMeMutation.mutate(trimmed);
  };

  const handleDownload = async () => {
    if (!token || isDownloading) return;
    setIsDownloading(true);
    try {
      const response = await fetch(`/api/public/invoice/${token}/pdf`);
      if (!response.ok) {
        throw new Error("Failed to generate PDF");
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Invoice-${invoice?.invoiceNumber || "download"}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast({
        title: "Download Failed",
        description:
          "We couldn't generate your PDF. Please try again in a moment.",
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const isSuccess = paymentState === "stripe_success" || paymentState === "paypal_success";
  const isConfirming = paymentState === "stripe_confirming";
  const primaryColor = branding?.primaryColor || null;
  const accentColor = branding?.accentColor || null;
  const payButtonStyle = primaryColor
    ? { backgroundColor: primaryColor, borderColor: primaryColor, color: "#fff" }
    : undefined;
  const accentStripStyle = accentColor || primaryColor
    ? { backgroundColor: accentColor || primaryColor || undefined }
    : undefined;

  if (isLoading || isConfirming) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center gap-4">
        <Loader2 className="w-10 h-10 animate-spin text-green-500" />
        {isConfirming && (
          <p className="text-gray-600 font-medium">Confirming your payment…</p>
        )}
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Invoice Not Found</h1>
          <p className="text-gray-600">This invoice may have been removed or the link is invalid.</p>
        </div>
      </div>
    );
  }

  if (isSuccess) {
    return <PaymentSuccessOverlay invoiceNumber={invoice.invoiceNumber || ""} />;
  }

  const eventRate = parseFloat(invoice.eventRate || "0");
  const discount = parseFloat(invoice.discount || "0");
  const lateFee = parseFloat(invoice.lateFee || "0");
  const totalAmount = eventRate - discount + lateFee;
  const hasPaymentOptions = paymentSettings?.stripeEnabled || paymentSettings?.paypalEnabled;
  const alreadyPaid = invoice.isPaid || invoice.status === "paid";

  return (
    <div className="min-h-screen bg-gray-100 py-4 sm:py-8 px-3 sm:px-0">
      {accentStripStyle && (
        <div
          className="fixed top-0 left-0 right-0 h-1 z-40 print:hidden"
          style={accentStripStyle}
          data-testid="strip-brand-accent"
        />
      )}
      <div className="max-w-2xl mx-auto">

        {/* PayPal Pending Banner */}
        {paymentState === "paypal_pending" && (
          <div className="mb-6 p-5 bg-blue-50 border border-blue-200 rounded-lg print:hidden" data-testid="paypal-pending-banner">
            <div className="flex items-start gap-3">
              <SiPaypal className="w-6 h-6 text-[#003087] mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900 mb-1">Complete Your PayPal Payment</h3>
                <p className="text-sm text-blue-700 mb-3">
                  Finish your payment in the PayPal window that opened, then click the button below to confirm.
                </p>
                {confirmError && (
                  <p className="text-sm text-red-600 mb-3">{confirmError}</p>
                )}
                <Button
                  onClick={handleConfirmPayPal}
                  disabled={confirmPaymentMutation.isPending}
                  className="bg-[#003087] hover:bg-[#002069] text-white"
                  data-testid="button-confirm-paypal"
                >
                  {confirmPaymentMutation.isPending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  I've Completed My PayPal Payment
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Already Paid Banner */}
        {alreadyPaid && paymentState === "idle" && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg print:hidden" data-testid="already-paid-banner">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-green-900">Invoice Paid</h3>
                <p className="text-sm text-green-700">This invoice has already been paid. Thank you!</p>
              </div>
            </div>
          </div>
        )}

        {/* Cancelled Banner */}
        {paymentState === "cancelled" && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg print:hidden">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-yellow-900">Payment Cancelled</h3>
                <p className="text-sm text-yellow-700">Your payment was cancelled. You can try again using the Pay Now button below.</p>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-center flex-wrap gap-4 mb-6 print:hidden">
          <Button
            size="lg"
            variant="outline"
            onClick={handleDownload}
            disabled={isDownloading}
            className="gap-2"
            data-testid="button-download-invoice"
          >
            {isDownloading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Preparing PDF…
              </>
            ) : (
              <>
                <Download className="w-5 h-5" />
                Download
              </>
            )}
          </Button>
          <Popover open={emailPopoverOpen} onOpenChange={setEmailPopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                size="lg"
                variant="outline"
                className="gap-2"
                data-testid="button-email-me-invoice"
              >
                <Mail className="w-5 h-5" />
                Email me a copy
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80" align="center">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleEmailMe();
                }}
                className="space-y-3"
              >
                <div className="space-y-1">
                  <Label htmlFor="email-me-address">Send PDF to</Label>
                  <Input
                    id="email-me-address"
                    type="email"
                    placeholder="you@example.com"
                    value={emailMeAddress}
                    onChange={(e) => setEmailMeAddress(e.target.value)}
                    required
                    data-testid="input-email-me-address"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full gap-2"
                  disabled={emailMeMutation.isPending || !emailMeAddress.trim()}
                  data-testid="button-send-email-me-invoice"
                >
                  {emailMeMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4" />
                      Send PDF
                    </>
                  )}
                </Button>
              </form>
            </PopoverContent>
          </Popover>
          {typeof navigator !== "undefined" && typeof (navigator as any).share === "function" && (
            <Button
              size="lg"
              variant="outline"
              className="gap-2"
              data-testid="button-share-invoice"
              onClick={async () => {
                try {
                  await (navigator as any).share({
                    title: invoice?.invoiceNumber ? `Invoice ${invoice.invoiceNumber}` : "Invoice",
                    text: "Here is your invoice.",
                    url: window.location.href,
                  });
                } catch (e) {
                  /* user cancelled */
                }
              }}
            >
              <Share2 className="w-5 h-5" />
              Share
            </Button>
          )}
          {!alreadyPaid && paymentState !== "paypal_pending" && (
            <Button
              size="lg"
              onClick={() => setShowPaymentDialog(true)}
              className="gap-2"
              style={payButtonStyle}
              data-testid="button-pay-now"
            >
              <CreditCard className="w-5 h-5" />
              Pay Now
            </Button>
          )}
        </div>

        {/* Task #241: Persistent "we delivered your copy" confirmation that
            survives page refresh (the timestamp + recipient are stored on
            the invoice itself, not in transient toast state). */}
        {invoice.lastEmailedAt && invoice.lastEmailedTo && (
          <div
            className="flex items-center justify-center gap-2 mb-6 -mt-2 text-sm text-green-700 print:hidden"
            data-testid="text-last-emailed-confirmation"
          >
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
            <span>
              Last sent to{" "}
              <span className="font-medium" data-testid="text-last-emailed-recipient">
                {invoice.lastEmailedTo}
              </span>
              {" "}at{" "}
              <span data-testid="text-last-emailed-timestamp">
                {new Date(invoice.lastEmailedAt).toLocaleString(undefined, {
                  month: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                })}
              </span>
            </span>
          </div>
        )}

        <div ref={invoiceRef} className="bg-white shadow-lg rounded-lg overflow-hidden">
          <InvoiceView
            invoice={invoice}
            lateFeeDescription={invoice.lateFeeDescription}
            branding={(invoice as any).invoiceBranding ?? null}
          />
        </div>
        {(() => {
          // Task #494: Show the workspace's own name here, not the legacy
          // hard-coded agency literal that used to leak across tenants.
          const invBranding = (invoice as any).invoiceBranding as
            | {
                invoiceTitle?: string | null;
                workspaceName?: string | null;
              }
            | undefined;
          const footerName =
            (branding?.workspaceName ?? "").trim() ||
            (invBranding?.workspaceName ?? "").trim() ||
            (invBranding?.invoiceTitle ?? "").trim();
          if (!footerName) return null;
          return (
            <p
              className="text-center text-sm text-gray-500 mt-6 print:hidden"
              data-testid="text-public-invoice-workspace-name"
            >
              {footerName}
            </p>
          );
        })()}
        <PoweredByAttribution
          show={(invoice as any).showPoweredByAttribution ?? true}
          className="text-xs text-gray-400 tracking-wide text-center mt-3 print:hidden"
        />
      </div>

      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">Payment Portal</DialogTitle>
            <DialogDescription className="text-center pt-2">
              Invoice #{invoice.invoiceNumber} • Total: ${totalAmount.toFixed(2)}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-4">
            {!paymentSettings ? (
              <div className="text-center py-4">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
                <p className="text-sm text-muted-foreground mt-2">Loading payment options...</p>
              </div>
            ) : hasPaymentOptions ? (
              <>
                {paymentSettings.paypalEnabled && (
                  paymentSettings.paypalSdkEnabled ? (
                    // Server-verified PayPal Checkout SDK flow
                    <div className="space-y-2">
                      <div className="flex items-center gap-3 px-1">
                        <div className="w-10 h-10 rounded-lg bg-[#003087]/10 flex items-center justify-center shrink-0">
                          <SiPaypal className="w-5 h-5 text-[#003087]" />
                        </div>
                        <div>
                          <h4 className="font-medium text-sm">Pay with PayPal</h4>
                          <p className="text-xs text-muted-foreground">Fast and secure payment</p>
                        </div>
                      </div>
                      {paypalSdkReady ? (
                        <div ref={paypalButtonContainerRef} data-testid="paypal-sdk-buttons" />
                      ) : (
                        <div className="flex items-center justify-center py-3 gap-2">
                          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">Loading PayPal...</span>
                        </div>
                      )}
                    </div>
                  ) : paymentSettings.paypalMeUsername ? (
                    // Legacy PayPal.me honor-system flow
                    <Card
                      className="hover-elevate cursor-pointer border-2 hover:border-[#003087] transition-colors"
                      onClick={handlePayWithPayPal}
                      data-testid="card-pay-paypal"
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-lg bg-[#003087]/10 flex items-center justify-center">
                              <SiPaypal className="w-6 h-6 text-[#003087]" />
                            </div>
                            <div>
                              <h4 className="font-medium">Pay with PayPal</h4>
                              <p className="text-sm text-muted-foreground">Fast and secure payment</p>
                            </div>
                          </div>
                          <ExternalLink className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </CardContent>
                    </Card>
                  ) : null
                )}

                {paymentSettings.stripeEnabled && (() => {
                  // Deposit/date-hold workflow — when the invoice has a
                  // deposit configured and it hasn't been recorded yet,
                  // show two side-by-side options. Otherwise fall back
                  // to the single "Pay with Card" card the legacy
                  // single-payment flow always rendered.
                  const inv: any = invoice;
                  const showDepositCta =
                    !!inv?.depositRequired && !inv?.depositPaidAt &&
                    Number(inv?.depositAmountCents || 0) > 0;
                  if (showDepositCta) {
                    const depositCents = Number(inv.depositAmountCents);
                    const depositLabel = `$${(depositCents / 100).toFixed(2)}`;
                    return (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Card
                          className={`hover-elevate cursor-pointer border-2 hover:border-[#635BFF] transition-colors ${stripeCheckoutMutation.isPending ? "opacity-50 pointer-events-none" : ""}`}
                          onClick={() => handlePayWithStripe("deposit")}
                          data-testid="card-pay-stripe-deposit"
                        >
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-[#635BFF]/10 flex items-center justify-center">
                                <SiStripe className="w-5 h-5 text-[#635BFF]" />
                              </div>
                              <div>
                                <h4 className="font-medium">Pay deposit</h4>
                                <p className="text-sm text-muted-foreground">{depositLabel} now</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                        <Card
                          className={`hover-elevate cursor-pointer border-2 hover:border-[#635BFF] transition-colors ${stripeCheckoutMutation.isPending ? "opacity-50 pointer-events-none" : ""}`}
                          onClick={() => handlePayWithStripe("full")}
                          data-testid="card-pay-stripe-full"
                        >
                          <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-[#635BFF]/10 flex items-center justify-center">
                                <SiStripe className="w-5 h-5 text-[#635BFF]" />
                              </div>
                              <div>
                                <h4 className="font-medium">Pay in full</h4>
                                <p className="text-sm text-muted-foreground">${totalAmount.toFixed(2)} now</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    );
                  }
                  return (
                    <Card
                      className={`hover-elevate cursor-pointer border-2 hover:border-[#635BFF] transition-colors ${stripeCheckoutMutation.isPending ? "opacity-50 pointer-events-none" : ""}`}
                      onClick={() => handlePayWithStripe("full")}
                      data-testid="card-pay-stripe"
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-lg bg-[#635BFF]/10 flex items-center justify-center">
                              {stripeCheckoutMutation.isPending ? (
                                <Loader2 className="w-6 h-6 text-[#635BFF] animate-spin" />
                              ) : (
                                <SiStripe className="w-6 h-6 text-[#635BFF]" />
                              )}
                            </div>
                            <div>
                              <h4 className="font-medium">{stripeCheckoutMutation.isPending ? "Redirecting..." : "Pay with Card"}</h4>
                              <p className="text-sm text-muted-foreground">Credit or debit card</p>
                            </div>
                          </div>
                          <ExternalLink className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })()}
              </>
            ) : (
              <div className="text-center py-4">
                <AlertCircle className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-lg font-medium">Payment portal coming soon!</p>
                <p className="text-sm text-muted-foreground mt-2">
                  We're working on a convenient online payment option.
                  In the meantime, please contact us for payment arrangements.
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-center pt-4">
            <Button
              variant="outline"
              onClick={() => setShowPaymentDialog(false)}
              data-testid="button-close-payment-dialog"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
